import React, { useContext, useEffect } from "react";

import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Pager } from "@components-shared/desktop/inputs";
import { Button } from "@components/buttons";
import { RawFilterData } from "@data-types";
import {
	DepositCustomPeriodInput,
	DepositPaymentMethodFilterSection,
	DepositPeriodsFilterSection,
	DepositTransactionStatusFilterSection,
} from "@features/payment/deposit/desktop/my-deposits/filter";
import { MyDepositsTable } from "@features/payment/deposit/desktop/my-deposits/table";
import { getUrlParamsAsObj } from "@state";
import { DepositContext } from "@state/context/payment/DepositContext";
import { MY_DEPOSITS_DEFAULT_FILTER } from "@state/stores/payment/deposit/DepositFilterStore";
import { observer } from "mobx-react-lite";

import DepositTabSwitcher from "./DepositTabSwitcher";

export default observer(function MyDepositsBody() {
	const {
		disposeCurrentDepositState,
		depositFilterStore: {
			setRawFilterData,
			setDefaultFilterData,
			rawData,
			pageNumber,
		},
		pageSize,
		totalItems,
		isStoreInitialized,
		isLoadingProcess,
	} = useContext(DepositContext);

	const methods = useForm();

	const { handleSubmit, reset } = methods;

	const { t } = useTranslation("payment");

	useEffect(() => {
		if (location.search !== "" && isStoreInitialized) {
			const params = getUrlParamsAsObj<RawFilterData>();
			reset(params);
			onSubmit(params);
		} else {
			setRawFilterData(MY_DEPOSITS_DEFAULT_FILTER);
			reset({
				period: MY_DEPOSITS_DEFAULT_FILTER.period,
				paymentMethodId: MY_DEPOSITS_DEFAULT_FILTER.paymentMethodId,
				transactionStatusId:
					MY_DEPOSITS_DEFAULT_FILTER.transactionStatusId,
			});
		}

		return () => {
			disposeCurrentDepositState();
			setDefaultFilterData();
		};
	}, [isStoreInitialized]);

	const onSubmit = (data: RawFilterData) => {
		setRawFilterData({
			...data,
		});
	};

	const onPageChange = (pageNumber: number) => {
		setRawFilterData({
			...rawData,
			pageNumber: pageNumber,
		});
	};

	return (
		<>
			<FormProvider {...methods}>
				<form onSubmit={handleSubmit(onSubmit)}>
					<div className="card--primary">
						<div className="card--primary__header">
							<h1 className="card--primary__title title--primary">
								{t<string>("DEPOSIT.TITLE")}
							</h1>
						</div>

						<DepositTabSwitcher />

						<div className="card--primary__body">
							<DepositPeriodsFilterSection />

							<DepositCustomPeriodInput />

							<DepositPaymentMethodFilterSection />

							<DepositTransactionStatusFilterSection />

							<Button
								type="submit"
								className="btn btn--base btn--accent"
								btnText={t<string>("COMMON.SUBMIT_BUTTON")}
								disabled={isLoadingProcess}
							></Button>
						</div>
					</div>
				</form>
			</FormProvider>

			<MyDepositsTable />

			<Pager
				pageNumber={pageNumber}
				pageSize={pageSize}
				totalItems={totalItems}
				onPageChange={onPageChange}
				withNumberInput
			/>
		</>
	);
});
